import React from 'react';
import { Helmet } from 'react-helmet';

const SEO = ({
    title = 'Mawathage Services — Sri Lanka Accommodation, Tours and Local Experiences',
    description = 'Discover Sri Lanka with Mawathage Services. Enjoy comfortable accommodation, traditional medicine, memorable tours, reliable vehicle rental, and warm local hospitality in Moragalla, Beruwala.',
    keywords = 'Mawathage Services, Sri Lanka accommodation, Beruwala tours, Moragalla travel, Sri Lankan traditional medicine, vehicle rental Sri Lanka, Bentota River tours, Moragalla Beach',
    image = 'https://www.mawatage-services.com/logo.png',
    url = 'https://www.mawatage-services.com',
    pathname = '/',
}) => {
    const pageUrl = `${url.replace(/\/$/, '')}${pathname.startsWith('/') ? pathname : `/${pathname}`}`;

    // Structured data for Mawathage Services
    const structuredData = [
        {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Mawathage Services",
            "url": "https://www.mawatage-services.com",
            "logo": "https://www.mawatage-services.com/logo.png",
            "email": "info@mawatage-services.com",
            "telephone": ["+94 74 274 2794", "+94 76 223 6647"],
            "sameAs": [
                "https://www.facebook.com/share/1BfGRTr59L/"
            ]
        },
        {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Mawathage Services",
            "url": "https://www.mawatage-services.com",
            "description": "Mawathage Services provides accommodation, Sri Lankan traditional medicine, tours, and vehicle rental in Moragalla, Beruwala, Sri Lanka.",
            "image": "https://www.mawatage-services.com/logo.png",
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "No. 61, Police Bungalow Road, Moragalla",
                "addressLocality": "Beruwala",
                "addressRegion": "Western Province",
                "postalCode": "12070",
                "addressCountry": "LK"
            },
            "telephone": "+94 74 274 2794",
            "email": "info@mawatage-services.com",
            "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Mawathage Services",
                "itemListElement": [
                    {
                        "@type": "Offer",
                        "itemOffered": {
                            "@type": "Service",
                            "name": "Accommodation",
                            "description": "Comfortable accommodation in Sri Lanka"
                        }
                    },
                    {
                        "@type": "Offer",
                        "itemOffered": {
                            "@type": "Service",
                            "name": "Sri Lankan Traditional Medicine",
                            "description": "Traditional holistic care rooted in local herbs and heritage"
                        }
                    },
                    {
                        "@type": "Offer",
                        "itemOffered": {
                            "@type": "Service",
                            "name": "Tours and Vehicle Rental",
                            "description": "Local tours and dependable transport across Sri Lanka"
                        }
                    }
                ]
            }
        },
        {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "url": "https://www.mawatage-services.com",
            "name": "Mawathage Services",
            "potentialAction": {
                "@type": "SearchAction",
                "target": "https://www.mawatage-services.com/search?q={search_term_string}",
                "query-input": "required name=search_term_string"
            }
        },
        {
            "@context": "https://schema.org",
            "@type": "TouristDestination",
            "name": "Mawathage Services - Moragalla, Beruwala",
            "description": "A local Sri Lankan destination for accommodation, traditional medicine, tours, and vehicle rental.",
            "url": "https://www.mawatage-services.com",
            "touristType": [
                "Wellness Seekers",
                "Yoga Enthusiasts",
                "Ayurveda Practitioners",
                "Beach Lovers",
                "Health Conscious Travelers"
            ]
        }
    ];

    return (
        <Helmet>
            <html lang="en" />
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <meta name="author" content="Mawathage Services" />
            <meta name="robots" content="index, follow, max-image-preview:large" />
            <meta name="googlebot" content="index, follow" />

            {/* Canonical – very important */}
            <link rel="canonical" href={pageUrl} />

            {/* Open Graph – better for social & WhatsApp sharing */}
            <meta property="og:locale" content="en_US" />
            <meta property="og:site_name" content="Mawathage Services" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content="Mawathage Services in Sri Lanka" />
            <meta property="og:url" content={pageUrl} />
            <meta property="og:type" content="website" />

            {/* Twitter / X Cards */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:site" content="@mawatageservices" />

            {/* Additional SEO Meta Tags */}
            <meta name="geo.region" content="LK-WP" />
            <meta name="geo.placename" content="Moragalla, Beruwala" />

            {/* JSON-LD – very powerful for rich results */}
            <script type="application/ld+json">
                {JSON.stringify(structuredData)}
            </script>
        </Helmet>
    );
};

export default SEO;